/**
 * activityService.ts — HTTP client for activity log endpoints.
 *
 * Wraps the /activity routes:
 *   - GET  /activity?page&limit&category&from&to&userId&search
 *   - POST /activity  (cashier login/logout, manual entries)
 *
 * Activity is the audit-log-lite from Sprint 1B Day 5+. Tracks user
 * logins/logouts, sales, stock movements, product CRUD, expenditure
 * approvals, etc.
 *
 * NOTE: Backend response is { activities: [...], pagination: {...} }
 * NOT { success, data, pagination } — different from most endpoints.
 *
 * BACKWARDS COMPATIBILITY: this file exports both the new contract
 * (`list`, `ActivityListFilters`) and legacy aliases (`getActivities`,
 * `createActivity`, `ActivityFilters`) so existing consumers
 * (authStore.ts, activityStore.ts) don't break. New code should use
 * the `list` method and `ActivityListFilters` type.
 */
import api from "./api";

// ─── Types ─────────────────────────────────────────────────────────

export interface ActivityUser {
  _id: string;
  name: string;
  email?: string;
}

export interface Activity {
  _id: string;
  action: string;
  description: string;
  user?: ActivityUser | string | null;
  category?: string;
  companyId: string;
  businessId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityListResponse {
  activities: Activity[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface ActivityListFilters {
  page?: number;
  limit?: number;
  category?: string;
  search?: string;
  from?: string; // ISO date
  to?: string; // ISO date
  userId?: string;
}

// Legacy alias — used by Zustand stores written before the rename.
// New code should use ActivityListFilters.
export type ActivityFilters = ActivityListFilters;

export interface CreateActivityInput {
  action: string;
  description: string;
  category?: string;
  metadata?: Record<string, unknown>;
}

// ─── API surface ────────────────────────────────────────────────────

export const activityService = {
  /**
   * List activities (paginated, filterable). Default limit 50.
   *
   * Used by Dashboard, ActionHistory, Header (via useActivities React
   * Query hook).
   */
  list: async (
    filters: ActivityListFilters = {},
  ): Promise<ActivityListResponse> => {
    const params: Record<string, string | number> = {};
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    if (filters.category && filters.category !== "all")
      params.category = filters.category;
    if (filters.search) params.search = filters.search;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.userId) params.userId = filters.userId;

    const res = await api.get<ActivityListResponse>("/activity", { params });
    return res.data;
  },

  /**
   * Legacy alias for `list`. Kept for Zustand stores written before the
   * rename (activityStore.ts). New code should call `list` directly.
   */
  getActivities: async (
    filters: ActivityListFilters = {},
  ): Promise<ActivityListResponse> => {
    return activityService.list(filters);
  },

  /**
   * Manually create an activity entry. Most activity is auto-logged by
   * backend services (sales, stock movements, etc.), but cashier login/
   * logout is logged from the frontend via this endpoint.
   *
   * Backend route: POST /activity. companyId + businessId + userId are
   * resolved from the JWT by middleware — frontend only sends the
   * activity payload.
   */
  createActivity: async (input: CreateActivityInput): Promise<Activity> => {
    const res = await api.post<{ activity: Activity }>("/activity", input);
    return res.data.activity;
  },
};

export default activityService;
